// Dungeon reward computation. Pure derivation from `DungeonTemplate` +
// `DungeonRunState`. The numbers feed two consumers:
//   - DungeonClearPage display (gold / xp / loot count summary)
//   - active-character store on boss victory (loot count → generateItem)
//
// gold/xp persistence on the character record arrives with Phase 4. For
// now they are display-only; the equipment/inventory drop is the one
// effect that does persist (via @renavora/content's generateItem and
// the existing character.inventory field).
//
// Numbers are暫定 (Phase 4 校正前提) — see docs/07 §7.8 (reward
// composition) and ADR 0027 §2.3 (rewardScale curve).
import { aggregateDungeonBoons } from './dungeon-boon-effects.js';
import { bossLevelBoost } from './dungeon-catalogue.js';
/**
 * Base gold per boss kill, pre-multiplier. Scaled by template.rewardScale
 * and capped by penalty. Round numbers picked so the Tier 1 (rewardScale=1.0)
 * baseline lands at 100 gold, matching docs/09 §9.4 economy assumptions.
 */
export const REWARD_BASE_GOLD = 100;
/** Base XP per boss kill, pre-multiplier. */
export const REWARD_BASE_XP = 50;
/**
 * Base loot count per boss kill, pre-multiplier. `Math.round(base * scale)`
 * keeps the count integer and stays ≥ 1 for any reasonable scale (the
 * compute function also enforces a floor of 1 so a 100% penalty still drops
 * a single consolation item — a docs/07 §7.5.2 "撤退ベース報酬" hook).
 */
export const REWARD_BASE_LOOT_COUNT = 3;
/** Lower bound on loot drops; keeps the clear screen meaningful at high penalty. */
export const REWARD_MIN_LOOT_COUNT = 1;
// ---------------------------------------------------------------------------
// Difficulty / level-dependent reward multiplier (P55-043 / ADR 0038 §2.3).
//
// Flat per-template rewards made "farm the easiest clearable dungeon" strictly
// optimal (V3-04). We scale gold / xp / loot by a multiplier `D(r)` derived
// from the ratio `r = heroLevel / effectiveEnemyLevel`:
//   - r ≤ 1 (at / under level): a clear is hard, so reward gets a bonus,
//     capped at REWARD_UNDER_LEVEL_CAP.
//   - r > 1 (over-levelled): a clear is trivial, so reward decays toward
//     REWARD_OVER_LEVEL_FLOOR — a Lv72 hero two-second-clearing a Tier 1
//     boss now earns ~floor× instead of the full payout.
// Coefficients are provisional and tuned via the realistic-ally balance
// harness; they live as named exports so the bench can reference them.
// ---------------------------------------------------------------------------
/** Under-level bonus slope: bonus = 1 + K·(1−r) for r ≤ 1. */
export const REWARD_UNDER_LEVEL_K = 0.5;
/** Maximum under-level bonus multiplier (clamps deep under-levelling). */
export const REWARD_UNDER_LEVEL_CAP = 1.5;
/** Over-level decay slope: multiplier = 1 / (1 + K·(r−1)) for r > 1. */
export const REWARD_OVER_LEVEL_K = 1.0;
/** Floor on the over-level multiplier so a trivial farm still drops a little. */
export const REWARD_OVER_LEVEL_FLOOR = 0.1;
/**
 * Difficulty multiplier for a clear at hero level `heroLevel` against an
 * encounter of effective level `effectiveEnemyLevel`. Pure, monotonic in `r`,
 * and clamped to `[REWARD_OVER_LEVEL_FLOOR, REWARD_UNDER_LEVEL_CAP]`. Defensive
 * clamps keep it finite for non-positive / NaN-adjacent inputs.
 */
export function levelDifficultyMultiplier(heroLevel, effectiveEnemyLevel) {
    const enemy = Math.max(1, effectiveEnemyLevel);
    const r = Math.max(0, heroLevel) / enemy;
    if (r <= 1) {
        return Math.min(REWARD_UNDER_LEVEL_CAP, 1 + REWARD_UNDER_LEVEL_K * (1 - r));
    }
    return Math.max(REWARD_OVER_LEVEL_FLOOR, 1 / (1 + REWARD_OVER_LEVEL_K * (r - 1)));
}
/**
 * Compute the reward summary for a finished run. Pure: no I/O, no
 * randomness. Loot identity (which items) is decided by a separate
 * `generateRunLoot` consumer keyed on `lootCount`.
 *
 * `effectiveScale` is clamped to a non-negative number, matching ADR 0026
 * §2.7 / docs/07 §7.5.1 where penaltyPct ≤ PENALTY_CAP = 0.5 means the
 * scale stays positive in normal play; the clamp is defensive against
 * future cap tweaks pushing penaltyPct ≥ 1.0.
 */
export function computeRunReward(template, run, opts = {}) {
    const effectiveScale = Math.max(0, template.rewardScale * (1 - run.penaltyPct));
    const boons = aggregateDungeonBoons(run.activeBoons);
    const levelMultiplier = opts.heroLevel === undefined
        ? 1
        : levelDifficultyMultiplier(opts.heroLevel, opts.effectiveEnemyLevel ??
            template.baseEnemyLevel + bossLevelBoost(template.tier));
    const scaled = effectiveScale * levelMultiplier;
    const lootCount = Math.max(REWARD_MIN_LOOT_COUNT, Math.round(REWARD_BASE_LOOT_COUNT * scaled));
    return {
        effectiveScale,
        levelMultiplier,
        goldEarned: Math.max(0, Math.floor(REWARD_BASE_GOLD * scaled * (1 + boons.goldRewardBonus))),
        // XP rides levelMultiplier only — see the RunReward.xpEarned doc (ADR 0042).
        xpEarned: Math.max(0, Math.floor(REWARD_BASE_XP * levelMultiplier * (1 + boons.xpRewardBonus))),
        lootCount,
    };
}
/**
 * Deterministic seed for boss-clear loot generation. Distinct from
 * `boonSeed` / `battleSeed` / `subtypeSeed` so re-rolling loot does not
 * leak into other channels and vice versa.
 *
 * Single-input variant (no coord) because boss loot is keyed to "run
 * cleared at all" — Floor 12 has exactly one node so a coord-bearing
 * version would always be `[12, 0]`.
 */
export function rewardSeed(runSeed) {
    // `0x517cc1b7` is a Murmur3 finalisation constant — distinct from the
    // `0xc6a4a793` / `0xdaa1bf8d` pair used by boonSeed. The trailing `>>> 0`
    // clamps the XOR back to 32-bit unsigned domain (Math.imul produces a
    // signed 32-bit int).
    return ((runSeed ^ 0x517cc1b7) ^ Math.imul(runSeed, 0x27d4eb2f)) >>> 0;
}
//# sourceMappingURL=dungeon-rewards.js.map